// ✅ FIXED schedulerHandlers.js - Using YYYY-MM-DD Format
import { sendToolResult, parseAzureResponse } from './Azurechatbot';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

/**
 * ✅ UPDATED: Handle get schedule with animated display
 */
export const handleGetSchedule = async (
  toolCallId, 
  args, 
  messageHistory, 
  setMessages, 
  tasksByDate,
  setTasksByDate,
  firestoreService
) => {
  console.log('📅 GET SCHEDULE:', args);
  
  // ✅ STEP 1: Show loading
  const loadingMsgId = Date.now();
  setMessages(prev => [...prev, {
    id: loadingMsgId,
    type: 'bot',
    content: 'Checking schedule...',
    isLoading: true,
    timestamp: new Date().toISOString()
  }]);
  
  try {
    // ✅ STEP 2: Load fresh data
    const freshCalendar = await loadCalendarFromStorage(firestoreService);
    
    const queryDate = parseRelativeDate(args.date || 'today');
    const dateKey = formatDateKey(queryDate);
    
    const events = freshCalendar[dateKey] || [];
    
    console.log('✅ Found', events.length, 'events');

    // ✅ Update state
    if (Object.keys(freshCalendar).length > 0) {
      setTasksByDate(freshCalendar);
    }

    // ✅ STEP 3: Remove loading
    setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));

    // ✅ STEP 4: Summary message
    const dateStr = queryDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });

    let summaryText;
    if (events.length === 0) {
      summaryText = `You don't have anything scheduled for ${dateStr}.`;
    } else if (events.length === 1) {
      summaryText = `Here's your schedule for ${dateStr}:`;
    } else {
      summaryText = `You have ${events.length} tasks scheduled for ${dateStr}:`;
    }

    const summaryMsgId = Date.now();
    setMessages(prev => [...prev, {
      id: summaryMsgId,
      type: 'bot',
      content: summaryText,
      timestamp: new Date().toISOString()
    }]);

// ✅ STEP 5: Add display (ONLY if events exist)
    if (events.length > 0) {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'bot',
          scheduleDisplay: {
            date: queryDate || new Date(), // ✅ ADD FALLBACK
            dateKey: dateKey,
            events: events,
            tasksByDate: freshCalendar,
            setTasksByDate: setTasksByDate,
            firestoreService: firestoreService
          },
          timestamp: new Date().toISOString()
        }]);
      }, 400);
    }
    // ✅ STEP 6: Silent tool result (NO response message)
    try {
      await sendToolResult(
        messageHistory,
        toolCallId,
        'get_schedule',
        args,
        {
          success: true,
          eventCount: events.length,
          summary: summaryText
        }
      );
    } catch (error) {
      console.log('Tool result failed (non-critical):', error);
    }
    
  } catch (error) {
    console.error('❌ Get schedule error:', error);
    setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));
    
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: `Sorry, I couldn't fetch your schedule. ${error.message}`,
      timestamp: new Date().toISOString()
    }]);
  }
};

/**
 * ============================================
 * Helper: Format date to YYYY-MM-DD
 * ============================================
 */
const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * ============================================
 * Helper: Parse relative dates
 * ============================================
 */
export const parseRelativeDate = (dateString) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (!dateString) return today;
  
  const lower = dateString.toLowerCase().trim();
  
  // TODAY
  if (lower === 'today' || lower === '') return today;
  
  // TOMORROW
  if (lower === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  }
  
  // NEXT [DAY]
  const nextDayMatch = lower.match(/next (monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
  if (nextDayMatch) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetDay = days.indexOf(nextDayMatch[1].toLowerCase());
    const currentDay = today.getDay();
    let daysToAdd = targetDay - currentDay;
    if (daysToAdd <= 0) daysToAdd += 7;
    
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysToAdd);
    return nextDate;
  }
  
  // THIS WEEK - return today + next 7 days
  if (lower.includes('this week')) {
    return today; // Will be handled separately
  }
  
  // Try to parse as date
  const parsed = new Date(dateString);
  if (!isNaN(parsed.getTime())) {
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }
  
  return today;
};



/**
 * ✅ Wait for user authentication
 */
const waitForUser = () => {
  return new Promise((resolve) => {
    const auth = getAuth();
    
    if (auth.currentUser) {
      console.log('✅ User already authenticated:', auth.currentUser.email);
      resolve(auth.currentUser);
      return;
    }
    
    console.log('⏳ Waiting for Firebase Auth...');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        console.log('✅ User authenticated:', user.email);
      } else {
        console.warn('⚠️ No user found after auth check');
      }
      resolve(user);
    });
  });
};

/**
 * ✅ Load calendar from Firestore (user-specific)
 */
export const loadCalendarFromStorage = async (firestoreService) => {
  try {
    const user = await waitForUser();
    
    if (!user) {
      console.log('⚠️ No user authenticated');
      return {};
    }

    if (!firestoreService) {
      console.log('⚠️ Firestore service not initialized yet');
      return {};
    }

    if (typeof firestoreService.waitForReady === 'function') {
      console.log('⏳ Waiting for Firestore service to be ready...');
      await firestoreService.waitForReady(5000);
    }

    if (!firestoreService.isReady || !firestoreService.isReady()) {
      console.log('⚠️ Firestore not ready after waiting');
      return {};
    }

    const userData = await firestoreService.getUserData();
    const calendar = userData?.calendar || {};
    
    // ✅ Clean old events on load AND convert old format to new
    const cleaned = cleanOldEvents(calendar);
    
    console.log('📂 Calendar loaded from Firestore:', Object.keys(cleaned).length, 'dates');
    return cleaned;
  } catch (error) {
    console.error('❌ Failed to load calendar:', error);
    return {};
  }
};

/**
 * ✅ Clean events older than 30 days AND convert old format to YYYY-MM-DD
 */
const cleanOldEvents = (calendar) => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
  
  const cleaned = {};
  
  Object.entries(calendar).forEach(([dateKey, events]) => {
    let eventDate;
    let newDateKey = dateKey;
    
    if (dateKey.includes('-')) {
      // ✅ Already in YYYY-MM-DD format
      const [year, month, day] = dateKey.split('-').map(Number);
      eventDate = new Date(year, month - 1, day);
    } else {
      // ❌ Old format (just day number) - convert it!
      const dayOfMonth = parseInt(dateKey);
      eventDate = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
      
      // Convert to new format
      newDateKey = formatDateKey(eventDate);
      console.log(`🔄 Converting old date key ${dateKey} → ${newDateKey}`);
    }
    
    // Keep only recent events or future events
    if (eventDate >= thirtyDaysAgo || eventDate > now) {
      cleaned[newDateKey] = events;
    } else {
      console.log(`🗑️ Cleaning old events from ${dateKey}`);
    }
  });
  
  return cleaned;
};

/**
 * ✅ NEW: Parse recurring event pattern from natural language
 */
const parseRecurringPattern = (description) => {
  const text = description.toLowerCase();
  
  // Check for "every [day]"
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayMatch = days.find(day => text.includes(`every ${day}`));
  
  if (dayMatch) {
    return {
      type: 'weekly',
      dayOfWeek: days.indexOf(dayMatch),
      dayName: dayMatch,
      interval: 1
    };
  }
  
  // Check for "every day" or "daily"
  if (text.includes('every day') || text.includes('daily')) {
    return {
      type: 'daily',
      interval: 1
    };
  }
  
  // Check for "every [number] days"
  const daysMatch = text.match(/every (\d+) days?/);
  if (daysMatch) {
    return {
      type: 'daily',
      interval: parseInt(daysMatch[1])
    };
  }
  
  return null;
};

/**
 * ✅ NEW: Generate recurring event instances
 */
const generateRecurringInstances = (baseEvent, pattern, startDate, weeksAhead = 8) => {
  const instances = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  
  const endDate = new Date(current);
  endDate.setDate(endDate.getDate() + (weeksAhead * 7));
  
  if (pattern.type === 'weekly') {
    // Generate weekly instances
    while (current <= endDate) {
      if (current.getDay() === pattern.dayOfWeek) {
        const dateKey = formatDateKey(current);
        instances.push({
          dateKey,
          event: {
            ...baseEvent,
            isRecurring: true,
            recurrenceId: baseEvent.recurrenceId,
            instanceDate: dateKey
          }
        });
      }
      current.setDate(current.getDate() + 1);
    }
  } else if (pattern.type === 'daily') {
    // Generate daily instances
    while (current <= endDate) {
      const dateKey = formatDateKey(current);
      instances.push({
        dateKey,
        event: {
          ...baseEvent,
          isRecurring: true,
          recurrenceId: baseEvent.recurrenceId,
          instanceDate: dateKey
        }
      });
      current.setDate(current.getDate() + pattern.interval);
    }
  }
  
  return instances;
};

/**
 * ✅ FIXED: Better event matching with fuzzy search
 */
const findEventByTitle = (tasksByDate, eventTitle) => {
  const lowerTitle = eventTitle.toLowerCase().trim();
  
  // Try exact match first
  for (const [dateKey, events] of Object.entries(tasksByDate)) {
    const exactIndex = events.findIndex(e => 
      e.text.toLowerCase().trim() === lowerTitle
    );
    
    if (exactIndex !== -1) {
      console.log('✅ Found exact match:', events[exactIndex].text);
      return {
        dateKey,
        eventIndex: exactIndex,
        event: events[exactIndex]
      };
    }
  }
  
  // Try partial match
  for (const [dateKey, events] of Object.entries(tasksByDate)) {
    const partialIndex = events.findIndex(e => 
      e.text.toLowerCase().includes(lowerTitle) || 
      lowerTitle.includes(e.text.toLowerCase())
    );
    
    if (partialIndex !== -1) {
      console.log('✅ Found partial match:', events[partialIndex].text);
      return {
        dateKey,
        eventIndex: partialIndex,
        event: events[partialIndex]
      };
    }
  }
  
  // Try word matching (match if any significant word matches)
  const titleWords = lowerTitle.split(/\s+/).filter(w => w.length > 2);
  
  for (const [dateKey, events] of Object.entries(tasksByDate)) {
    const wordMatchIndex = events.findIndex(e => {
      const eventWords = e.text.toLowerCase().split(/\s+/);
      const matches = titleWords.filter(word => 
        eventWords.some(ew => ew.includes(word) || word.includes(ew))
      );
      return matches.length >= Math.max(1, Math.floor(titleWords.length / 2));
    });
    
    if (wordMatchIndex !== -1) {
      console.log('✅ Found word match:', events[wordMatchIndex].text);
      return {
        dateKey,
        eventIndex: wordMatchIndex,
        event: events[wordMatchIndex]
      };
    }
  }
  
  console.log('❌ No match found for:', eventTitle);
  return null;
};

/**
 * ✅ Save calendar to Firestore (user-specific)
 */
export const saveCalendarToStorage = async (tasksByDate, firestoreService) => {
  try {
    // ✅ ADD THIS CHECK
    if (!tasksByDate || typeof tasksByDate !== 'object') {
      console.warn('⚠️ Invalid tasksByDate:', tasksByDate);
      return false;
    }

    const user = await waitForUser();
    
    if (!user) {
      console.warn('⚠️ No user authenticated');
      return false;
    }
    
    if (!firestoreService) {
      console.warn('⚠️ Firestore service not available');
      return false;
    }
    
    const isServiceReady = typeof firestoreService.isReady === 'function' 
      ? firestoreService.isReady() 
      : firestoreService.isReady;

    if (!isServiceReady) {
      console.warn('⚠️ Firestore not ready');
      return false;
    }

    // Clean old events before saving
    const cleaned = cleanOldEvents(tasksByDate || {}); // ✅ ADD FALLBACK
    
    console.log('💾 Saving calendar to Firestore:', {
      totalDates: Object.keys(cleaned).length,
      totalEvents: Object.values(cleaned).reduce((sum, events) => sum + events.length, 0)
    });
    
    await firestoreService.updateUserData({
      calendar: cleaned,
      calendarUpdatedAt: new Date().toISOString()
    });
    
    console.log('✅ Calendar saved successfully');
    return true;
  } catch (error) {
    console.error('❌ Failed to save calendar:', error);
    return false;
  }
};


// ✅ HELPER: Format time for speech (FIXED VERSION)
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  try {
    // Handle both "HH:MM" and "HH:MM:SS" formats
    const parts = timeStr.split(':');
    const hours = parseInt(parts[0]);
    const minutes = parts[1];
    
    if (isNaN(hours)) return timeStr;
    
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    
    return `${displayHour}:${minutes} ${ampm}`;
  } catch (e) {
    console.error('❌ Error formatting time:', e, 'Input:', timeStr);
    return timeStr; // Return original if parsing fails
  }
};

// ✅ NEW FUNCTION: Smart date parsing
export const parseScheduleRequest = (query) => {
  const lower = query.toLowerCase().trim();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // TOMORROW
  if (lower.includes('tomorrow')) {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return {
      type: 'single',
      date: tomorrow,
      dateKey: formatDateKey(tomorrow),
      label: 'tomorrow'
    };
  }
  
  // NEXT WEEK
  if (lower.includes('next week')) {
    const startOfNextWeek = new Date(today);
    const currentDay = today.getDay();
    const daysUntilNextMonday = currentDay === 0 ? 1 : 8 - currentDay;
    startOfNextWeek.setDate(today.getDate() + daysUntilNextMonday);
    
    const endOfNextWeek = new Date(startOfNextWeek);
    endOfNextWeek.setDate(startOfNextWeek.getDate() + 6);
    
    return {
      type: 'range',
      startDate: startOfNextWeek,
      endDate: endOfNextWeek,
      label: 'next week'
    };
  }
  
  // THIS WEEK
  if (lower.includes('this week')) {
    const startOfWeek = new Date(today);
    const currentDay = today.getDay();
    const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1;
    startOfWeek.setDate(today.getDate() - daysFromMonday);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return {
      type: 'range',
      startDate: startOfWeek,
      endDate: endOfWeek,
      label: 'this week'
    };
  }
  
  // TODAY (default)
  return {
    type: 'single',
    date: today,
    dateKey: formatDateKey(today),
    label: 'today'
  };
};

// ✅ NEW FUNCTION: Format schedule for podcast dialogue
export const formatScheduleForPodcast = (scheduleData, query = 'today') => {
  if (!scheduleData || Object.keys(scheduleData).length === 0) {
    return {
      hasEvents: false,
      summary: "Your calendar is empty.",
      eventCount: 0
    };
  }
  
  const parsedRequest = parseScheduleRequest(query);
  
  if (parsedRequest.type === 'single') {
    // Single day request (today/tomorrow)
    const events = scheduleData[parsedRequest.dateKey] || [];
    
    if (events.length === 0) {
      return {
        hasEvents: false,
        summary: `You don't have anything scheduled for ${parsedRequest.label}.`,
        eventCount: 0,
        dateLabel: parsedRequest.label
      };
    }
    
    const dateObj = parsedRequest.date;
    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
    const monthDay = dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    
    let summary = `For ${parsedRequest.label}, ${dayName}, ${monthDay}, you have `;
    
    if (events.length === 1) {
      const event = events[0];
      summary += `${event.text}`;
      if (event.time) {
        summary += ` at ${formatTime(event.time)}`;
      }
    } else {
      summary += `${events.length} things scheduled. `;
      
      // List first 3 events
      events.slice(0, 3).forEach((event, idx) => {
        if (idx === 0) {
          summary += `First, ${event.text}`;
        } else if (idx === events.length - 1 || idx === 2) {
          summary += `, and ${event.text}`;
        } else {
          summary += `, then ${event.text}`;
        }
        
        if (event.time) {
          summary += ` at ${formatTime(event.time)}`;
        }
      });
      
      if (events.length > 3) {
        summary += `, plus ${events.length - 3} more events`;
      }
    }
    
    summary += '.';
    
    return {
      hasEvents: true,
      summary: summary,
      eventCount: events.length,
      dateLabel: parsedRequest.label,
      events: events
    };
    
  } else if (parsedRequest.type === 'range') {
    // Week range request (this week/next week)
    const allEvents = [];
    const currentDate = new Date(parsedRequest.startDate);
    
    while (currentDate <= parsedRequest.endDate) {
      const dateKey = formatDateKey(currentDate);
      const dayEvents = scheduleData[dateKey] || [];
      
      if (dayEvents.length > 0) {
        allEvents.push({
          date: new Date(currentDate),
          dateKey: dateKey,
          events: dayEvents
        });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    if (allEvents.length === 0) {
      return {
        hasEvents: false,
        summary: `You don't have anything scheduled for ${parsedRequest.label}.`,
        eventCount: 0,
        dateLabel: parsedRequest.label
      };
    }
    
    const totalEventCount = allEvents.reduce((sum, day) => sum + day.events.length, 0);
    
    let summary = `Looking at ${parsedRequest.label}, you have ${totalEventCount} events total. `;
    
    // Summarize by day
    allEvents.forEach((day, idx) => {
      const dayName = day.date.toLocaleDateString('en-US', { weekday: 'long' });
      const eventCount = day.events.length;
      
      if (idx === 0) {
        summary += `On ${dayName}, you have `;
      } else if (idx === allEvents.length - 1) {
        summary += `, and on ${dayName}, you have `;
      } else {
        summary += `, on ${dayName}, you have `;
      }
      
      if (eventCount === 1) {
        summary += `${day.events[0].text}`;
        if (day.events[0].time) {
          summary += ` at ${formatTime(day.events[0].time)}`;
        }
      } else {
        summary += `${eventCount} things scheduled`;
      }
    });
    
    summary += '.';
    
    return {
      hasEvents: true,
      summary: summary,
      eventCount: totalEventCount,
      dateLabel: parsedRequest.label,
      allEvents: allEvents
    };
  }
};
export const handleScheduleEvent = async (
  toolCallId, 
  args, 
  messageHistory, 
  setMessages, 
  setTasksByDate, 
  firestoreService
) => {
  console.log('📅 SCHEDULE EVENT:', args);
  
  const loadingMsgId = Date.now();
  setMessages(prev => [...prev, {
    id: loadingMsgId,
    type: 'bot',
    content: `📅 Adding "${args.title}" to your calendar...`,
    isLoading: true,
    timestamp: new Date().toISOString()
  }]);
  
  try {
    const eventDate = parseRelativeDate(args.date);
    const dateKey = formatDateKey(eventDate);
    
    // ✅ Check for recurring pattern
    const fullDescription = `${args.title} ${args.description || ''}`.toLowerCase();
    const recurringPattern = parseRecurringPattern(fullDescription);
    
    const baseEvent = {
      text: args.title,
      time: args.time,
      completed: false,
      isNew: true,
      description: args.description,
      createdAt: new Date().toISOString()
    };
    
    let updatedCalendar;
    let instanceCount = 1; // ✅ DECLARE OUTSIDE IF BLOCK
    
    if (recurringPattern) {
      // ✅ Handle recurring event
      console.log('🔄 Creating recurring event:', recurringPattern);
      
      const recurrenceId = `recurring_${Date.now()}`;
      baseEvent.isRecurring = true;
      baseEvent.recurrenceId = recurrenceId;
      baseEvent.recurrencePattern = recurringPattern;
      
      const instances = generateRecurringInstances(baseEvent, recurringPattern, eventDate);
      instanceCount = instances.length; // ✅ STORE COUNT
      
      setTasksByDate(prev => {
        const updated = { ...prev };
        
        instances.forEach(({ dateKey: instDateKey, event }) => {
          updated[instDateKey] = [...(updated[instDateKey] || []), event];
        });
        
        updatedCalendar = updated;
        saveCalendarToStorage(updated, firestoreService);
        return updated;
      });
      
      // Remove loading
      setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));
      
      // Success message
      const dayName = recurringPattern.dayName || 'day';
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        content: `✅ Set up recurring event: "${args.title}" every ${dayName} at ${args.time}. Created ${instanceCount} instances for the next 8 weeks.`,
        showCalendarButton: true,
        timestamp: new Date().toISOString()
      }]);
      
    } else {
      // ✅ Handle single event
      setTasksByDate(prev => {
        const updated = {
          ...prev,
          [dateKey]: [...(prev[dateKey] || []), baseEvent]
        };
        updatedCalendar = updated;
        saveCalendarToStorage(updated, firestoreService);
        return updated;
      });
      
      // Remove loading
      setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));
      
      // Success message
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        content: `✅ Added "${args.title}" to ${eventDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric' 
        })} at ${args.time}`,
        showCalendarButton: true,
        timestamp: new Date().toISOString()
      }]);
    }
    
    // Send tool result
    await sendToolResult(
      messageHistory,
      toolCallId,
      'schedule_event',
      args,
      { 
        success: true,
        isRecurring: !!recurringPattern,
        instanceCount: instanceCount, // ✅ NOW DEFINED
        message: `Event "${args.title}" scheduled`
      },
      updatedCalendar
    );
    
    // Clear "isNew" flag after animation
    setTimeout(() => {
      setTasksByDate(prev => {
        const updated = {};
        for (const [key, events] of Object.entries(prev)) {
          updated[key] = events.map(e => ({ ...e, isNew: false }));
        }
        saveCalendarToStorage(updated, firestoreService);
        return updated;
      });
    }, 1000);
    
  } catch (error) {
    console.error('❌ Schedule error:', error);
    setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: `Sorry, couldn't add the event: ${error.message}`,
      timestamp: new Date().toISOString()
    }]);
  }
};

/**
 * ✅ FIXED: Handle update event with YYYY-MM-DD format
 */
export const handleUpdateEvent = async (
  toolCallId, 
  args, 
  messageHistory, 
  setMessages, 
  setTasksByDate, 
  firestoreService
) => {
  console.log('✏️ UPDATE EVENT:', args);
  
  const loadingMsgId = Date.now();
  setMessages(prev => [...prev, {
    id: loadingMsgId,
    type: 'bot',
    content: '✏️ Updating event...',
    isLoading: true,
    timestamp: new Date().toISOString()
  }]);
  
  try {
    let updatedCalendar;
    let oldEventText;
    
    setTasksByDate(prev => {
      const found = findEventByTitle(prev, args.eventTitle);
      
      if (!found) {
        throw new Error(`Could not find event: "${args.eventTitle}"`);
      }
      
      const { dateKey, eventIndex, event } = found;
      oldEventText = event.text;
      const newCalendar = { ...prev };
      
      // Remove from old location
      newCalendar[dateKey] = prev[dateKey].filter((_, idx) => idx !== eventIndex);
      if (newCalendar[dateKey].length === 0) delete newCalendar[dateKey];
      
      // Calculate new date key
      const targetDateKey = args.newDate 
        ? formatDateKey(parseRelativeDate(args.newDate))
        : dateKey;
      
      console.log('📆 Moving event from', dateKey, 'to', targetDateKey);
      
      const updatedEvent = {
        ...event,
        text: args.newTitle || event.text,
        time: args.newTime || event.time,
        description: args.newDescription || event.description,
        isNew: true,
        updatedAt: new Date().toISOString()
      };
      
      newCalendar[targetDateKey] = [...(newCalendar[targetDateKey] || []), updatedEvent];
      
      updatedCalendar = newCalendar;
      saveCalendarToStorage(newCalendar, firestoreService);
      return newCalendar;
    });
    
    // Remove loading
    setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));
    
    // Success message
    const changes = [];
    if (args.newTitle) changes.push(`title to "${args.newTitle}"`);
    if (args.newTime) changes.push(`time to ${args.newTime}`);
    if (args.newDate) changes.push(`date to ${parseRelativeDate(args.newDate).toLocaleDateString()}`);
    
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: `Updated "${oldEventText}"${changes.length ? ': ' + changes.join(', ') : ''}`,
      showCalendarButton: true,
      timestamp: new Date().toISOString()
    }]);
    
    await sendToolResult(
      messageHistory,
      toolCallId,
      'update_event',
      args,
      { success: true, changes: changes },
      updatedCalendar
    );
    
    // Clear "isNew" flag
    setTimeout(() => {
      setTasksByDate(prev => {
        const updated = {};
        for (const [key, events] of Object.entries(prev)) {
          updated[key] = events.map(e => ({ ...e, isNew: false }));
        }
        saveCalendarToStorage(updated, firestoreService);
        return updated;
      });
    }, 1000);
    
  } catch (error) {
    console.error('❌ Update error:', error);
    setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: `${error.message}. Try being more specific with the event name.`,
      timestamp: new Date().toISOString()
    }]);
  }
};

/**
 * ✅ Handle delete event - already works with YYYY-MM-DD
 */
export const handleDeleteEvent = async (
  toolCallId, 
  args, 
  messageHistory, 
  setMessages, 
  setTasksByDate, 
  firestoreService
) => {
  console.log('🗑️ DELETE EVENT:', args);
  
  const loadingMsgId = Date.now();
  setMessages(prev => [...prev, {
    id: loadingMsgId,
    type: 'bot',
    content: '🗑️ Removing...',
    isLoading: true
  }]);
  
  try {
    let updatedCalendar;
    let deletedEvent;
    
    setTasksByDate(prev => {
      const found = findEventByTitle(prev, args.eventTitle);
      
      if (!found) throw new Error('Event not found');
      
      const { dateKey, eventIndex, event } = found;
      deletedEvent = event;
      
      const newCalendar = { ...prev };
      newCalendar[dateKey] = prev[dateKey].filter((_, idx) => idx !== eventIndex);
      
      if (newCalendar[dateKey].length === 0) delete newCalendar[dateKey];
      
      updatedCalendar = newCalendar;
      saveCalendarToStorage(newCalendar, firestoreService);
      return newCalendar;
    });
    
    setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: `✅ Removed "${deletedEvent.text}"`,
      showCalendarButton: true
    }]);
    
    await sendToolResult(
      messageHistory,
      toolCallId,
      'delete_event',
      args,
      { success: true },
      updatedCalendar
    );
    
  } catch (error) {
    console.error('❌ Delete error:', error);
    setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: `Couldn't find "${args.eventTitle}"`
    }]);
  }
};


/**
 * Update event completion
 */
export const updateEventCompletion = async (
  tasksByDate, 
  dateKey, 
  eventIndex, 
  completed, 
  firestoreService
) => {
  const updated = {
    ...tasksByDate,
    [dateKey]: tasksByDate[dateKey].map((event, idx) => 
      idx === eventIndex ? { ...event, completed } : event
    )
  };
  
  await saveCalendarToStorage(updated, firestoreService);
  return updated;
};

/**
 * Delete an event
 */
export const deleteEvent = async (
  tasksByDate, 
  dateKey, 
  eventIndex, 
  firestoreService
) => {
  const updated = {
    ...tasksByDate,
    [dateKey]: tasksByDate[dateKey].filter((_, idx) => idx !== eventIndex)
  };
  
  if (updated[dateKey].length === 0) delete updated[dateKey];
  
  await saveCalendarToStorage(updated, firestoreService);
  return updated;
};

/**
 * ✅ NEW: Auto-sync external calendar event to Firestore
 */
export const autoSyncEventToCalendar = async (
  eventData,
  dateKey,
  firestoreService
) => {
  try {
    const user = await waitForUser();
    
    if (!user || !firestoreService?.isReady()) {
      console.warn('⚠️ Cannot auto-sync: no user or Firestore not ready');
      return false;
    }

    // Get current calendar
    const userData = await firestoreService.getUserData();
    const currentCalendar = userData?.calendar || {};
    
    // Add event to correct date
    const updatedCalendar = {
      ...currentCalendar,
      [dateKey]: [...(currentCalendar[dateKey] || []), eventData]
    };
    
    // Save back to Firestore
    await firestoreService.updateUserData({
      calendar: updatedCalendar,
      calendarUpdatedAt: new Date().toISOString()
    });
    
    console.log('✅ Auto-synced event to Firestore:', dateKey);
    return true;
  } catch (error) {
    console.error('❌ Failed to auto-sync event:', error);
    return false;
  }
};

export default {
  handleScheduleEvent,
  handleUpdateEvent,
  handleDeleteEvent,
  handleGetSchedule,
  parseRelativeDate,
  loadCalendarFromStorage,
  saveCalendarToStorage,
  updateEventCompletion,
  deleteEvent,
  autoSyncEventToCalendar,
  parseScheduleRequest,
  formatScheduleForPodcast,
  formatTime 
};